import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Send, Save } from 'lucide-react';
import { nfseService, type NfseItemInput, type NfsePagamentoInput, type NfseResultado } from '@/features/fiscal/services/nfseService';
import { getApiErrorMessage } from '@/lib/apiClient';
import { useAuth } from '@/hooks/useAuth';
import { PessoaSearchCombobox } from '@/components/PessoaSearchCombobox';
import { CitySearchCombobox } from '@/components/CitySearchCombobox';
import { ServicoSearchCombobox } from '@/components/ServicoSearchCombobox';
import { personService } from '@/features/pessoas/services/personService';

// ── Constants ──────────────────────────────────────────────────────────────

const NATUREZAS = [
  { id: 1, label: '1 – Tributação no município' },
  { id: 2, label: '2 – Tributação fora do município' },
  { id: 3, label: '3 – Isenção' },
  { id: 4, label: '4 – Imune' },
  { id: 5, label: '5 – Exigibilidade suspensa por decisão judicial' },
  { id: 6, label: '6 – Exigibilidade suspensa por procedimento administrativo' },
];

const FORMAS_PAG = [
  { codigo: '01', descricao: 'Dinheiro' },
  { codigo: '02', descricao: 'Cheque' },
  { codigo: '03', descricao: 'Cartão de Crédito' },
  { codigo: '04', descricao: 'Cartão de Débito' },
  { codigo: '05', descricao: 'Crédito Loja' },
  { codigo: '10', descricao: 'Vale Alimentação' },
  { codigo: '11', descricao: 'Vale Refeição' },
  { codigo: '15', descricao: 'Boleto Bancário' },
  { codigo: '90', descricao: 'Sem Pagamento' },
  { codigo: '99', descricao: 'Outro' },
];

// ── Types ──────────────────────────────────────────────────────────────────

interface Cabecalho {
  rps:                       number;
  serie:                     string;
  dtEmissao:                 string;
  dtSaida:                   string;
  naturezaOperacaoId:        number;
  naturezaOperacao:          string;
  isAmbienteProducao:        boolean;
  itemListaServico:          string;
  codigoTributacaoMunicipal: string;
  codigoObra:                string;
  codigoArt:                 string;
  incentivadorCultural:      boolean;
  optanteSimplesNacional:    boolean;
  descricaoServico:          string;
  pedido:                    string;
}

interface Tomador {
  clienteId:         number;
  nomeRazao:         string;
  cnpjCpf:           string;
  inscricaoMunicipal:string;
  inscricaoEstadual: string;
  email:             string;
  telefone:          string;
  endereco:          string;
  numero:            string;
  complemento:       string;
  bairro:            string;
  cidade:            string;
  uf:                string;
  cep:               string;
  cidadeIbge:        number;
}

interface Totalizadores {
  aliquotaIss:                number;
  valorDeducoes:              number;
  valorDescontoCondicionado:  number;
  valorDescontoIncondicionado:number;
  perRetPis:                  number;
  perRetCofins:               number;
  perRetInss:                 number;
  perRetIr:                   number;
  perRetCsll:                 number;
  perRetIssqn:                number;
}

// ── Helpers ────────────────────────────────────────────────────────────────

const today = () => new Date().toISOString().slice(0, 10);

const fmt = (v: number) =>
  v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Sub-components ─────────────────────────────────────────────────────────

function Field({
  label, children, className,
}: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`flex flex-col gap-1 ${className ?? ''}`}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

// ── Tab: Cabeçalho ─────────────────────────────────────────────────────────

function TabCabecalho({ cab, onChange }: { cab: Cabecalho; onChange: (c: Partial<Cabecalho>) => void }) {
  return (
    <div className="grid grid-cols-12 gap-4 p-4">
      <Field label="RPS Nº" className="col-span-2">
        {/* Numerado no servidor (sob lock, por série) ao salvar — o valor enviado é ignorado. */}
        <Input value="Automático" readOnly disabled className="bg-muted/40" />
      </Field>
      <Field label="Série" className="col-span-1">
        <Input value={cab.serie} onChange={e => onChange({ serie: e.target.value })} maxLength={4} />
      </Field>
      <Field label="Dt. Emissão" className="col-span-2">
        <Input type="date" value={cab.dtEmissao} onChange={e => onChange({ dtEmissao: e.target.value })} />
      </Field>
      <Field label="Dt. Saída" className="col-span-2">
        <Input type="date" value={cab.dtSaida} onChange={e => onChange({ dtSaida: e.target.value })} />
      </Field>
      <Field label="Ambiente" className="col-span-2 justify-center pt-4">
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={cab.isAmbienteProducao}
            onCheckedChange={v => onChange({ isAmbienteProducao: !!v })}
          />
          Produção
        </label>
      </Field>

      <Field label="Natureza da Operação" className="col-span-7">
        <select
          className="h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm"
          value={cab.naturezaOperacaoId}
          onChange={e => {
            const nat = NATUREZAS.find(n => n.id === +e.target.value);
            onChange({ naturezaOperacaoId: +e.target.value, naturezaOperacao: nat?.label ?? '' });
          }}
        >
          {NATUREZAS.map(n => (
            <option key={n.id} value={n.id}>{n.label}</option>
          ))}
        </select>
      </Field>
      <Field label="Incentivador Cultural" className="col-span-2 justify-center pt-4">
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={cab.incentivadorCultural}
            onCheckedChange={v => onChange({ incentivadorCultural: !!v })}
          />
          Sim
        </label>
      </Field>
      <Field label="Simples Nacional" className="col-span-3 justify-center pt-4">
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={cab.optanteSimplesNacional}
            onCheckedChange={v => onChange({ optanteSimplesNacional: !!v })}
          />
          Optante
        </label>
      </Field>

      <Field label="Item da Lista de Serviço (LC 116)" className="col-span-3">
        <Input value={cab.itemListaServico} onChange={e => onChange({ itemListaServico: e.target.value })} />
      </Field>
      <Field label="Cód. Tributação Municipal" className="col-span-3">
        <Input value={cab.codigoTributacaoMunicipal} onChange={e => onChange({ codigoTributacaoMunicipal: e.target.value })} />
      </Field>
      <Field label="Código de Obra" className="col-span-3">
        <Input value={cab.codigoObra} onChange={e => onChange({ codigoObra: e.target.value })} />
      </Field>
      <Field label="Código ART" className="col-span-3">
        <Input value={cab.codigoArt} onChange={e => onChange({ codigoArt: e.target.value })} />
      </Field>

      <Field label="Número do Pedido" className="col-span-4">
        <Input value={cab.pedido} onChange={e => onChange({ pedido: e.target.value })} />
      </Field>

      <Field label="Discriminação dos Serviços" className="col-span-12">
        <Textarea
          rows={4}
          value={cab.descricaoServico}
          onChange={e => onChange({ descricaoServico: e.target.value })}
          placeholder="Descreva os serviços prestados..."
        />
      </Field>
    </div>
  );
}

// ── Tab: Tomador ───────────────────────────────────────────────────────────

function TabTomador({ tom, onChange }: { tom: Tomador; onChange: (t: Partial<Tomador>) => void }) {
  // Escolher o cliente preenche o tomador com o cadastro (campos seguem editáveis).
  const selecionarCliente = async (clienteId: number) => {
    onChange({ clienteId });
    try {
      const p = await personService.getById(clienteId);
      const end = p.enderecos?.find(e => e.tipo === 'comercial') ?? p.enderecos?.[0];
      onChange({
        clienteId,
        nomeRazao:          p.nomeRazao ?? '',
        cnpjCpf:            p.cpfCnpj ?? '',
        inscricaoMunicipal: p.inscricaoMunicipal ?? '',
        inscricaoEstadual:  p.inscricaoEstadual ?? '',
        email:              p.email ?? '',
        telefone:           p.fone || p.celular || '',
        ...(end ? {
          endereco:    end.logradouro ?? '',
          numero:      end.numero ?? '',
          complemento: end.complemento ?? '',
          bairro:      end.bairro ?? '',
          cidade:      end.cidade ?? '',
          uf:          end.uf ?? '',
          cep:         end.cep ?? '',
          cidadeIbge:  end.cidadeIbgeId || 0,
        } : {}),
      });
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Não foi possível carregar o cliente.'));
    }
  };

  return (
    <div className="grid grid-cols-12 gap-4 p-4">
      <Field label="Cliente" className="col-span-12">
        <PessoaSearchCombobox
          value={tom.clienteId || undefined}
          onChange={selecionarCliente}
          filter="cliente"
          placeholder="Buscar cliente pelo nome ou CPF/CNPJ..."
        />
      </Field>
      <Field label="Nome / Razão Social" className="col-span-8">
        <Input value={tom.nomeRazao} onChange={e => onChange({ nomeRazao: e.target.value })} />
      </Field>
      <Field label="CNPJ / CPF" className="col-span-4">
        <Input value={tom.cnpjCpf} onChange={e => onChange({ cnpjCpf: e.target.value })} />
      </Field>
      <Field label="Inscrição Municipal" className="col-span-3">
        <Input value={tom.inscricaoMunicipal} onChange={e => onChange({ inscricaoMunicipal: e.target.value })} />
      </Field>
      <Field label="Inscrição Estadual" className="col-span-3">
        <Input value={tom.inscricaoEstadual} onChange={e => onChange({ inscricaoEstadual: e.target.value })} />
      </Field>
      <Field label="E-mail" className="col-span-4">
        <Input type="email" value={tom.email} onChange={e => onChange({ email: e.target.value })} />
      </Field>
      <Field label="Telefone" className="col-span-2">
        <Input value={tom.telefone} onChange={e => onChange({ telefone: e.target.value })} />
      </Field>

      <Field label="Endereço" className="col-span-6">
        <Input value={tom.endereco} onChange={e => onChange({ endereco: e.target.value })} />
      </Field>
      <Field label="Número" className="col-span-2">
        <Input value={tom.numero} onChange={e => onChange({ numero: e.target.value })} />
      </Field>
      <Field label="Complemento" className="col-span-4">
        <Input value={tom.complemento} onChange={e => onChange({ complemento: e.target.value })} />
      </Field>
      <Field label="Bairro" className="col-span-3">
        <Input value={tom.bairro} onChange={e => onChange({ bairro: e.target.value })} />
      </Field>
      <Field label="Cidade" className="col-span-4">
        <CitySearchCombobox
          value={tom.cidade || null}
          onSelect={c => onChange({ cidade: c.nome, uf: c.uf ?? tom.uf, cidadeIbge: c.codigoIbge ?? 0 })}
        />
      </Field>
      <Field label="UF" className="col-span-1">
        <Input value={tom.uf} readOnly className="bg-muted/40" />
      </Field>
      <Field label="CEP" className="col-span-2">
        <Input value={tom.cep} onChange={e => onChange({ cep: e.target.value })} />
      </Field>
      <Field label="IBGE" className="col-span-2">
        <Input value={tom.cidadeIbge || ''} readOnly className="bg-muted/40" title="Preenchido pela cidade" />
      </Field>
    </div>
  );
}

// ── Tab: Serviços ──────────────────────────────────────────────────────────

function TabServicos({
  itens,
  onChange,
}: {
  itens: NfseItemInput[];
  onChange: (itens: NfseItemInput[]) => void;
}) {
  const addItem = () =>
    onChange([
      ...itens,
      { servicoId: 0, descricaoServico: '', codigoLC: '', unidade: 'UN',
        isTributavel: true, quantidade: 1, valorUnitario: 0, desconto: 0, aliquotaIss: 0 },
    ]);

  const updateItem = (idx: number, patch: Partial<NfseItemInput>) =>
    onChange(itens.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const removeItem = (idx: number) =>
    onChange(itens.filter((_, i) => i !== idx));

  return (
    <div className="p-4 space-y-3">
      <div className="flex justify-end">
        <Button size="sm" variant="outline" onClick={addItem}>
          <Plus className="mr-1 h-4 w-4" /> Adicionar Serviço
        </Button>
      </div>

      {itens.length === 0 && (
        <p className="text-center text-muted-foreground py-8 text-sm">
          Nenhum serviço adicionado.
        </p>
      )}

      {itens.map((it, idx) => (
        <div key={idx} className="border rounded-md p-3 grid grid-cols-12 gap-3 bg-muted/20">
          <Field label="Serviço" className="col-span-4">
            <ServicoSearchCombobox
              value={it.servicoId || null}
              descricaoInicial={it.descricaoServico || null}
              onSelect={s => {
                if (!s) { updateItem(idx, { servicoId: 0 }); return; }
                updateItem(idx, {
                  servicoId:        s.id,
                  descricaoServico: s.descricao,
                  codigoLC:         s.itemListaServico || it.codigoLC,
                  aliquotaIss:      s.aliquotaIss || it.aliquotaIss,
                  ...(!it.valorUnitario && s.valor ? { valorUnitario: s.valor } : {}),
                });
              }}
            />
          </Field>
          <Field label="Descrição" className="col-span-3">
            <Input value={it.descricaoServico} onChange={e => updateItem(idx, { descricaoServico: e.target.value })} />
          </Field>
          <Field label="Cód. LC" className="col-span-2">
            <Input value={it.codigoLC ?? ''} onChange={e => updateItem(idx, { codigoLC: e.target.value })} />
          </Field>
          <Field label="Unidade" className="col-span-1">
            <Input value={it.unidade ?? ''} onChange={e => updateItem(idx, { unidade: e.target.value })} />
          </Field>
          <Field label="Tributável" className="col-span-1 justify-center pt-4">
            <Checkbox
              checked={it.isTributavel}
              onCheckedChange={v => updateItem(idx, { isTributavel: !!v })}
            />
          </Field>
          <div className="col-span-1 flex items-end justify-end pb-1">
            <Button size="icon" variant="ghost" onClick={() => removeItem(idx)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>

          <Field label="Quantidade" className="col-span-2">
            <Input type="number" step="0.01" value={it.quantidade} onChange={e => updateItem(idx, { quantidade: +e.target.value })} />
          </Field>
          <Field label="Valor Unitário" className="col-span-3">
            <Input type="number" step="0.01" value={it.valorUnitario} onChange={e => updateItem(idx, { valorUnitario: +e.target.value })} />
          </Field>
          <Field label="Desconto" className="col-span-2">
            <Input type="number" step="0.01" value={it.desconto} onChange={e => updateItem(idx, { desconto: +e.target.value })} />
          </Field>
          <Field label="Alíquota ISS (%)" className="col-span-2">
            <Input type="number" step="0.01" value={it.aliquotaIss} onChange={e => updateItem(idx, { aliquotaIss: +e.target.value })} />
          </Field>
          <Field label="Total" className="col-span-3">
            <div className="h-9 flex items-center px-3 rounded-md border border-input bg-muted/40 text-sm font-medium">
              {fmt((it.quantidade * it.valorUnitario) - it.desconto)}
            </div>
          </Field>
        </div>
      ))}
    </div>
  );
}

// ── Tab: Totalizadores ─────────────────────────────────────────────────────

function TabTotalizadores({
  itens,
  tot,
  onChange,
}: {
  itens: NfseItemInput[];
  tot:   Totalizadores;
  onChange: (t: Partial<Totalizadores>) => void;
}) {
  const totalBruto    = itens.reduce((s, i) => s + i.quantidade * i.valorUnitario - i.desconto, 0);
  const baseCalculo   = Math.max(0, totalBruto - tot.valorDeducoes - tot.valorDescontoIncondicionado);
  const valorIss      = Math.round(baseCalculo * tot.aliquotaIss) / 100;
  const retPis        = Math.round(totalBruto * tot.perRetPis)    / 100;
  const retCofins     = Math.round(totalBruto * tot.perRetCofins) / 100;
  const retInss       = Math.round(totalBruto * tot.perRetInss)   / 100;
  const retIr         = Math.round(totalBruto * tot.perRetIr)     / 100;
  const retCsll       = Math.round(totalBruto * tot.perRetCsll)   / 100;
  const retIss        = Math.round(totalBruto * tot.perRetIssqn)  / 100;
  const totalRet      = retPis + retCofins + retInss + retIr + retCsll + retIss;
  const totalNota     = totalBruto - totalRet - tot.valorDescontoCondicionado - tot.valorDescontoIncondicionado;

  const NumField = ({ label, value, field, readOnly }: {
    label: string; value: number; field?: keyof Totalizadores; readOnly?: boolean;
  }) => (
    <Field label={label} className="col-span-3">
      {readOnly ? (
        <div className="h-9 flex items-center px-3 rounded-md border border-input bg-muted/40 text-sm font-medium">
          {fmt(value)}
        </div>
      ) : (
        <Input
          type="number"
          step="0.01"
          value={value}
          onChange={e => field && onChange({ [field]: +e.target.value })}
        />
      )}
    </Field>
  );

  return (
    <div className="grid grid-cols-12 gap-4 p-4">
      <NumField label="Total Bruto de Serviços" value={totalBruto} readOnly />
      <NumField label="(-) Deduções / Abatimentos" value={tot.valorDeducoes} field="valorDeducoes" />
      <NumField label="(-) Desc. Incondicionado" value={tot.valorDescontoIncondicionado} field="valorDescontoIncondicionado" />
      <NumField label="(-) Desc. Condicionado" value={tot.valorDescontoCondicionado} field="valorDescontoCondicionado" />

      <Field label="Alíquota ISS (%)" className="col-span-3">
        <Input type="number" step="0.01" value={tot.aliquotaIss}
          onChange={e => onChange({ aliquotaIss: +e.target.value })} />
      </Field>
      <NumField label="Valor ISS Calculado" value={valorIss} readOnly />
      <div className="col-span-6" />

      <div className="col-span-12">
        <p className="text-xs font-semibold text-muted-foreground mb-2">Retenções na Fonte (%)</p>
      </div>
      <Field label="PIS (%)" className="col-span-2">
        <Input type="number" step="0.01" value={tot.perRetPis} onChange={e => onChange({ perRetPis: +e.target.value })} />
      </Field>
      <NumField label="Valor Ret. PIS" value={retPis} readOnly />
      <Field label="COFINS (%)" className="col-span-2">
        <Input type="number" step="0.01" value={tot.perRetCofins} onChange={e => onChange({ perRetCofins: +e.target.value })} />
      </Field>
      <NumField label="Valor Ret. COFINS" value={retCofins} readOnly />
      <div className="col-span-2" />

      <Field label="INSS (%)" className="col-span-2">
        <Input type="number" step="0.01" value={tot.perRetInss} onChange={e => onChange({ perRetInss: +e.target.value })} />
      </Field>
      <NumField label="Valor Ret. INSS" value={retInss} readOnly />
      <Field label="IR (%)" className="col-span-2">
        <Input type="number" step="0.01" value={tot.perRetIr} onChange={e => onChange({ perRetIr: +e.target.value })} />
      </Field>
      <NumField label="Valor Ret. IR" value={retIr} readOnly />
      <div className="col-span-2" />

      <Field label="CSLL (%)" className="col-span-2">
        <Input type="number" step="0.01" value={tot.perRetCsll} onChange={e => onChange({ perRetCsll: +e.target.value })} />
      </Field>
      <NumField label="Valor Ret. CSLL" value={retCsll} readOnly />
      <Field label="ISSQN Ret. (%)" className="col-span-2">
        <Input type="number" step="0.01" value={tot.perRetIssqn} onChange={e => onChange({ perRetIssqn: +e.target.value })} />
      </Field>
      <NumField label="Valor Ret. ISSQN" value={retIss} readOnly />
      <div className="col-span-2" />

      <div className="col-span-9" />
      <div className="col-span-3 border-t pt-3">
        <p className="text-xs text-muted-foreground">Total Líquido da Nota</p>
        <p className="text-2xl font-bold text-primary">R$ {fmt(totalNota)}</p>
      </div>
    </div>
  );
}

// ── Tab: Formas de Pagamento ───────────────────────────────────────────────

function TabPagamentos({
  pagamentos,
  totalNota,
  onChange,
}: {
  pagamentos: NfsePagamentoInput[];
  totalNota:  number;
  onChange:   (p: NfsePagamentoInput[]) => void;
}) {
  const addPag = () =>
    onChange([...pagamentos, { formaPagamento: '01', nomePagamento: 'Dinheiro', valor: 0 }]);

  const updatePag = (idx: number, patch: Partial<NfsePagamentoInput>) =>
    onChange(pagamentos.map((p, i) => (i === idx ? { ...p, ...patch } : p)));

  const removePag = (idx: number) => onChange(pagamentos.filter((_, i) => i !== idx));

  const totalPago = pagamentos.reduce((s, p) => s + p.valor, 0);

  return (
    <div className="p-4 space-y-3">
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          Total da nota: <span className="font-semibold text-foreground">R$ {fmt(totalNota)}</span>
          {' '}| Informado: <span className="font-semibold">R$ {fmt(totalPago)}</span>
          {' '}| Restante: <span className={`font-semibold ${totalNota - totalPago > 0.005 ? 'text-destructive' : 'text-green-600'}`}>
            R$ {fmt(Math.max(0, totalNota - totalPago))}
          </span>
        </div>
        <Button size="sm" variant="outline" onClick={addPag}>
          <Plus className="mr-1 h-4 w-4" /> Adicionar Pagamento
        </Button>
      </div>

      {pagamentos.length === 0 && (
        <p className="text-center text-muted-foreground py-8 text-sm">
          Nenhuma forma de pagamento informada.
        </p>
      )}

      {pagamentos.map((p, idx) => (
        <div key={idx} className="border rounded-md p-3 grid grid-cols-12 gap-3 bg-muted/20">
          <Field label="Forma de Pagamento" className="col-span-4">
            <select
              className="h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm"
              value={p.formaPagamento}
              onChange={e => {
                const fp = FORMAS_PAG.find(f => f.codigo === e.target.value);
                updatePag(idx, { formaPagamento: e.target.value, nomePagamento: fp?.descricao });
              }}
            >
              {FORMAS_PAG.map(f => (
                <option key={f.codigo} value={f.codigo}>{f.codigo} – {f.descricao}</option>
              ))}
            </select>
          </Field>
          <Field label="Valor" className="col-span-3">
            <Input type="number" step="0.01" value={p.valor}
              onChange={e => updatePag(idx, { valor: +e.target.value })} />
          </Field>
          <div className="col-span-1 flex items-end pb-1">
            <Button size="icon" variant="ghost" onClick={() => removePag(idx)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
          <div className="col-span-4" />
        </div>
      ))}
    </div>
  );
}

// ── Resultado da transmissão ───────────────────────────────────────────────

function ResultadoTransmissao({ resultado }: { resultado: NfseResultado }) {
  const ok = resultado.sucesso && resultado.status !== 'ERRO';
  return (
    <div className={`rounded-md border p-4 text-sm ${ok ? 'border-green-500 bg-green-50 dark:bg-green-950/20' : 'border-destructive bg-destructive/10'}`}>
      <p className="font-semibold mb-1">{ok ? 'Transmitido com sucesso' : 'Falha na transmissão'}</p>
      <p>Status: <strong>{resultado.status}</strong></p>
      <p>{resultado.motivo}</p>
      {resultado.protocolo  && <p>Protocolo: <strong>{resultado.protocolo}</strong></p>}
      {resultado.numeroNfse && <p>Número NFS-e: <strong>{resultado.numeroNfse}</strong></p>}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

export default function EmissaoNfse() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const empresaId = user?.empresa?.id ?? 0;

  const [cab, setCab] = useState<Cabecalho>({
    rps: 1, serie: 'A', dtEmissao: today(), dtSaida: today(),
    naturezaOperacaoId: 1, naturezaOperacao: '1 – Tributação no município',
    isAmbienteProducao: false, itemListaServico: '', codigoTributacaoMunicipal: '',
    codigoObra: '', codigoArt: '', incentivadorCultural: false,
    optanteSimplesNacional: false, descricaoServico: '', pedido: '',
  });

  const [tom, setTom] = useState<Tomador>({
    clienteId: 0, nomeRazao: '', cnpjCpf: '', inscricaoMunicipal: '',
    inscricaoEstadual: '', email: '', telefone: '', endereco: '', numero: '',
    complemento: '', bairro: '', cidade: '', uf: '', cep: '', cidadeIbge: 0,
  });

  const [itens, setItens] = useState<NfseItemInput[]>([]);

  const [tot, setTot] = useState<Totalizadores>({
    aliquotaIss: 0, valorDeducoes: 0, valorDescontoCondicionado: 0,
    valorDescontoIncondicionado: 0, perRetPis: 0, perRetCofins: 0,
    perRetInss: 0, perRetIr: 0, perRetCsll: 0, perRetIssqn: 0,
  });

  const [pagamentos, setPagamentos] = useState<NfsePagamentoInput[]>([]);
  const [isSaving, setIsSaving]     = useState(false);
  const [isEmitindo, setIsEmitindo] = useState(false);
  const [resultado, setResultado]   = useState<NfseResultado | null>(null);

  const totalBruto = itens.reduce((s, i) => s + i.quantidade * i.valorUnitario - i.desconto, 0);
  const totalNota  = Math.max(0, totalBruto
    - (itens.reduce((s, i) => s + Math.round(i.quantidade * i.valorUnitario * i.aliquotaIss) / 100, 0))
    - tot.perRetPis    / 100 * totalBruto
    - tot.perRetCofins / 100 * totalBruto
    - tot.perRetInss   / 100 * totalBruto
    - tot.perRetIr     / 100 * totalBruto
    - tot.perRetCsll   / 100 * totalBruto
    - tot.perRetIssqn  / 100 * totalBruto
    - tot.valorDescontoCondicionado
    - tot.valorDescontoIncondicionado
  );

  const buildRequest = () => ({
    empresaId:                  empresaId ?? 0,
    rps:                        cab.rps,
    serie:                      cab.serie,
    dtEmissao:                  cab.dtEmissao,
    dtSaida:                    cab.dtSaida,
    naturezaOperacaoId:         cab.naturezaOperacaoId,
    naturezaOperacao:           cab.naturezaOperacao,
    isAmbienteProducao:         cab.isAmbienteProducao,
    clienteId:                  tom.clienteId,
    nomeRazao:                  tom.nomeRazao,
    cnpjCpf:                    tom.cnpjCpf,
    inscricaoMunicipal:         tom.inscricaoMunicipal,
    inscricaoEstadual:          tom.inscricaoEstadual,
    email:                      tom.email,
    telefone:                   tom.telefone,
    endereco:                   tom.endereco,
    numero:                     tom.numero,
    complemento:                tom.complemento,
    bairro:                     tom.bairro,
    cidade:                     tom.cidade,
    uf:                         tom.uf,
    cep:                        tom.cep,
    cidadeIbge:                 tom.cidadeIbge,
    itemListaServico:           cab.itemListaServico,
    codigoTributacaoMunicipal:  cab.codigoTributacaoMunicipal,
    codigoObra:                 cab.codigoObra,
    codigoArt:                  cab.codigoArt,
    incentivadorCultural:       cab.incentivadorCultural,
    optanteSimplesNacional:     cab.optanteSimplesNacional,
    aliquotaIss:                tot.aliquotaIss,
    valorDeducoes:              tot.valorDeducoes,
    valorDescontoCondicionado:  tot.valorDescontoCondicionado,
    valorDescontoIncondicionado:tot.valorDescontoIncondicionado,
    perRetPis:                  tot.perRetPis,
    perRetCofins:               tot.perRetCofins,
    perRetInss:                 tot.perRetInss,
    perRetIr:                   tot.perRetIr,
    perRetCsll:                 tot.perRetCsll,
    perRetIssqn:                tot.perRetIssqn,
    descricaoServico:           cab.descricaoServico,
    pedido:                     cab.pedido,
    itens,
    formasPagamento:            pagamentos,
  });

  const handleSalvar = async () => {
    if (itens.length === 0) { toast.error('Adicione ao menos um serviço.'); return; }
    setIsSaving(true);
    try {
      const res = await nfseService.criar(buildRequest());
      toast.success('RPS salvo com sucesso');
      setResultado(res);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setIsSaving(false);
    }
  };

  const handleEmitir = async () => {
    if (itens.length === 0) { toast.error('Adicione ao menos um serviço.'); return; }
    setIsEmitindo(true);
    setResultado(null);
    try {
      const criado = await nfseService.criar(buildRequest());
      if (!criado.sucesso) {
        toast.error(criado.motivo ?? 'Erro ao salvar RPS');
        setResultado(criado);
        return;
      }
      toast.info('RPS criado, transmitindo...');
      const transmitido = await nfseService.transmitir(criado.notaId);
      setResultado(transmitido);
      if (transmitido.sucesso) toast.success('NFS-e transmitida');
      else toast.warning(transmitido.motivo ?? 'Verifique o status da transmissão');
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setIsEmitindo(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-6 py-3 bg-background">
        <h1 className="text-lg font-semibold">Nova NFS-e — Emissão de Nota Fiscal de Serviço</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/fiscal/nfse')}>
            Voltar
          </Button>
          <Button variant="outline" onClick={handleSalvar} disabled={isSaving || isEmitindo}>
            {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Salvar RPS
          </Button>
          <Button onClick={handleEmitir} disabled={isSaving || isEmitindo}>
            {isEmitindo ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Salvar e Emitir
          </Button>
        </div>
      </div>

      {/* Resultado */}
      {resultado && (
        <div className="px-6 py-3">
          <ResultadoTransmissao resultado={resultado} />
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="cabecalho" className="flex-1 overflow-hidden flex flex-col">
        <TabsList className="mx-6 mt-4 w-fit">
          <TabsTrigger value="cabecalho">Cabeçalho</TabsTrigger>
          <TabsTrigger value="tomador">Tomador</TabsTrigger>
          <TabsTrigger value="servicos">Serviços ({itens.length})</TabsTrigger>
          <TabsTrigger value="totalizadores">Totalizadores</TabsTrigger>
          <TabsTrigger value="pagamentos">Pagamentos ({pagamentos.length})</TabsTrigger>
        </TabsList>

        <div className="flex-1 overflow-y-auto">
          <TabsContent value="cabecalho" className="mt-0">
            <TabCabecalho cab={cab} onChange={p => setCab(prev => ({ ...prev, ...p }))} />
          </TabsContent>
          <TabsContent value="tomador" className="mt-0">
            <TabTomador tom={tom} onChange={p => setTom(prev => ({ ...prev, ...p }))} />
          </TabsContent>
          <TabsContent value="servicos" className="mt-0">
            <TabServicos itens={itens} onChange={setItens} />
          </TabsContent>
          <TabsContent value="totalizadores" className="mt-0">
            <TabTotalizadores itens={itens} tot={tot} onChange={p => setTot(prev => ({ ...prev, ...p }))} />
          </TabsContent>
          <TabsContent value="pagamentos" className="mt-0">
            <TabPagamentos pagamentos={pagamentos} totalNota={totalNota} onChange={setPagamentos} />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
